import { type ReactiveController, type ReactiveElement } from "lit";

import {
  Query,
  QueryClient,
  QueryObserver,
  type QueryObserverOptions,
  type QueryObserverResult,
} from "@tanstack/query-core";

//import { isProduction } from "../../apps/api/helpers/isProduction";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity, // Never auto-refresh due to staleness
      refetchOnWindowFocus: false, // Never auto-refresh on window focus
      refetchOnReconnect: false, // Never auto-refresh on network reconnect
      retry: false, // Prevent automatic retries
    },
  },
});

declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: import("@tanstack/query-core").QueryClient;
  }
}
if (typeof window !== "undefined") {
  window.__TANSTACK_QUERY_CLIENT__ = queryClient; // Expose for debugging
}

/**
 * Reactive controller for managing data queries with TanStack Query
 * Integrates with Lit's reactive system for automatic re-renders
 */
export class DataQueryController<TData> implements ReactiveController {
  private observer?: QueryObserver<TData, Error>;
  private unsubscribe?: () => void;

  public result!: QueryObserverResult<TData, Error>;
  public query!: Query<TData, Error, TData, readonly unknown[]>;

  constructor(
    private host: ReactiveElement,
    private getQueryOptions: () => QueryObserverOptions<TData>,
  ) {
    this.host.addController(this);
  }

  hostConnected(): void {
    this.createObserver();
  }
  hostDisconnected(): void {
    this.cleanup();
  }

  hostUpdate(): void {
    // Update observer options if they have changed
    if (this.observer) {
      const options = this.getQueryOptions();
      this.observer.setOptions(options);
    }
  }

  private createObserver(): void {
    const options = this.getQueryOptions();

    this.observer = new QueryObserver<TData, Error>(queryClient, options);

    this.unsubscribe = this.observer.subscribe((result) => {
      this.result = result;
      if (this.observer) this.query = this.observer.getCurrentQuery();

      this.host.requestUpdate();
    });
    this.result = this.observer.getCurrentResult();
    this.query = this.observer.getCurrentQuery();
  }

  private cleanup(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = undefined;
    }
    this.observer = undefined;
  }

  /**
   * Manually trigger a refetch of the query
   */
  refetch(): void {
    this.observer?.refetch();
  }

  /**
   * Invalidate the query, causing it to refetch
   */
  invalidate(): void {
    const options = this.getQueryOptions();
    queryClient.invalidateQueries({ queryKey: options.queryKey });
  }
}
